import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { ChoreographerWrapper } from '../message-renderer-provider/choreographer-wrapper';
import { UnmountCleanup } from '../unmount-cleanup';
import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';
import { type MessageCategory } from '@atlassian/post-office-context/message-context';
import { type AdditionalMetadata } from '@post-office/message-template-contracts/message-instance-event';

/**
 * Options for {@link WaitForTriggerFunction}.
 *
 * Object identity is deliberately non-load-bearing. `waitForTrigger` is a synchronous function
 * call during render — the options object is destructured at function entry and discarded. It is
 * never compared, stored, or used as a hook dependency, so callers do NOT need to memoize the
 * options object itself with `useMemo` to avoid downstream re-renders/effect re-fires.
 *
 * Per-field memoization (e.g. `useCallback` for `onMessageShown` and `onTeardown`) is still a
 * good practice for the usual React reasons — to keep downstream component props stable and
 * avoid unnecessary effect re-fires inside child components — but that concern is independent
 * of this options object's identity. Notably, `onTeardown` itself is identity-safe via the
 * ref-sync inside `<UnmountCleanup>`: only the latest function captured at render time is run
 * at unmount.
 */
export type WaitForTriggerOptions = {
	isTriggerActive: boolean;
	messageContent: ReactNode;
	/**
	 * Fires once after the choreographer approves and the message has mounted (i.e. is shown to the
	 * user).
	 */
	onMessageShown?: () => void;
	additionalMetadataFromFrontend?: AdditionalMetadata;
	/**
	 * Optional idempotent teardown that runs when the approved UI subtree unmounts (e.g. message
	 * stopped, dismissed, or the non-choreographed product path tears down). Use this to reset a
	 * product trigger so the message can be retriggered. Prefer idempotent work — this can run
	 * during React Strict Mode double-effects.
	 */
	onTeardown?: () => void;
};

/**
 * Type for the waitForTrigger function with attached ref for validation
 */
export type WaitForTriggerFunction = {
	(options: WaitForTriggerOptions): JSX.Element | null;
	__calledRef?: React.MutableRefObject<boolean>;
};

/**
 * Component that calls messageInstanceViewed, renders messageSuccessComponent, and calls onShow callback once when mounted
 */
const OnChoreographerApproved = ({
	children,
	onShow,
	messageSuccessComponent,
	additionalMetadataFromFrontend = {},
}: {
	additionalMetadataFromFrontend?: AdditionalMetadata;
	children: ReactNode;
	onShow?: () => void;
	messageSuccessComponent?: JSX.Element;
}) => {
	const hasShownRef = useRef(false);
	const { messageInstanceViewed } = useMessageInstanceEvent();

	useEffect(() => {
		if (hasShownRef.current) return;
		hasShownRef.current = true;
		void messageInstanceViewed(additionalMetadataFromFrontend);
		onShow?.();
		// Intentional empty deps: this effect must fire exactly once after the first commit.
		// The values captured here are the ones the caller passed at mount, which is the desired
		// "as of when the message was shown" semantic — if a caller subsequently passes a new
		// onShow or metadata, we should NOT fire again. Using non-empty deps would also cause
		// this effect to re-register on every render whenever a caller passes an unmemoized
		// `additionalMetadataFromFrontend={{ ... }}` literal, even though the body is a no-op
		// after first run. Strict-Mode double-invocation is still handled by hasShownRef.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			{messageSuccessComponent}
			{children}
		</>
	);
};

/**
 * Creates the waitForTrigger function for triggered messages.
 * This function handles all choreography and analytics wrapping automatically.
 * The ref for validation is attached to the function as __calledRef.
 *
 * @param hasTrigger - Whether this message has a trigger (triggerType !== 'onPageLoad')
 * @param messageSuccessComponent - Optional component to render when choreographer approves (for UFO tracking)
 * @returns Object containing waitForTrigger function (with attached ref)
 */
export const useWaitForTrigger = ({
	hasTrigger,
	messageSuccessComponent,
	messageInstanceId,
	messageTemplateId,
	messageCategory,
}: {
	hasTrigger: boolean;
	messageSuccessComponent?: JSX.Element;
	messageInstanceId: string;
	messageTemplateId: string;
	messageCategory: MessageCategory;
}): {
	waitForTrigger: WaitForTriggerFunction | undefined;
} => {
	const waitForTriggerCalledRef = useRef(false);

	// Create waitForTrigger function that wraps content with choreography
	const waitForTriggerCallback: WaitForTriggerFunction = useCallback(
		({
			isTriggerActive,
			messageContent,
			onMessageShown,
			additionalMetadataFromFrontend,
			onTeardown,
		}): JSX.Element | null => {
			// Mark that this function was called
			waitForTriggerCalledRef.current = true;

			// If isTriggerActive is false, don't claim choreographer slot yet
			if (!isTriggerActive) {
				return null;
			}

			const messageContentWithOptionalTeardown =
				onTeardown !== undefined ? (
					<UnmountCleanup onCleanup={onTeardown}>{messageContent}</UnmountCleanup>
				) : (
					messageContent
				);

			return (
				<ChoreographerWrapper
					messageId={messageInstanceId!}
					messageTemplateId={messageTemplateId!}
					messageCategory={messageCategory}
				>
					<OnChoreographerApproved
						onShow={onMessageShown}
						additionalMetadataFromFrontend={additionalMetadataFromFrontend}
						messageSuccessComponent={messageSuccessComponent}
					>
						{messageContentWithOptionalTeardown}
					</OnChoreographerApproved>
				</ChoreographerWrapper>
			);
		},
		[messageInstanceId, messageTemplateId, messageCategory, messageSuccessComponent],
	);

	const waitForTrigger: WaitForTriggerFunction | undefined = hasTrigger
		? waitForTriggerCallback
		: undefined; // Non-triggered messages don't get this prop

	// Attach the ref to the function as a property
	if (waitForTrigger) {
		waitForTrigger.__calledRef = waitForTriggerCalledRef;
	}

	return {
		waitForTrigger,
	};
};
